import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { apiDefaultFetcher, apiPostFetcher } from '@/common/api/fetcher'
import { UgcApiPath } from '@/common/api/params/ugc'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import PageTitle from '@/components/view/molecules/title/PageTitle'

interface SiteHealthScore {
  site_id: number
  site_name: string
  total_score: number
  login_frequency: number
  collection: number
  ab_test: number
  cvr: number
  alert: boolean
  cs_owner: string | null
  calculated_at: string | null
}

interface AllHealthScoresResponse {
  sites: SiteHealthScore[]
}

const SCORE_AXES: {
  label: string
  key: keyof Pick<SiteHealthScore, 'login_frequency' | 'collection' | 'ab_test' | 'cvr'>
  max: number
}[] = [
  { label: 'ログイン', key: 'login_frequency', max: 25 },
  { label: '収集', key: 'collection', max: 25 },
  { label: 'A/Bテスト', key: 'ab_test', max: 25 },
  { label: 'CVR', key: 'cvr', max: 25 }
]

const scoreHex = (score: number): string => {
  if (score >= 70) return '#27ae60'
  if (score >= 40) return '#f59e0b'
  return '#e74c3c'
}

const scoreLabel = (score: number): string => {
  if (score >= 70) return '良好'
  if (score >= 40) return '注意'
  return '要対応'
}

const SiteUsagePage = () => {
  const { data, isLoading, mutate } = useSWR<AllHealthScoresResponse>(
    UgcApiPath.GET_ALL_HEALTH_SCORES,
    apiDefaultFetcher,
    { revalidateOnMount: true }
  )

  const { trigger: recalculate, isMutating } = useSWRMutation(
    UgcApiPath.RECALCULATE_ALL,
    apiPostFetcher,
    { onSuccess: () => mutate() }
  )

  const sites = data?.sites ?? []

  return (
    <Main type={MainLayoutType.FULL} bottomSpace>
      <PageTitle
        title='サイト状況'
        description='全サイトの利用状況をスコアリングし、CS支援の優先度を可視化します。'
        rightNode={
          <Button
            color={ButtonColor.PRIMARY}
            size='large'
            loading={isMutating}
            onClick={() => recalculate({})}
          >
            全サイト再計算
          </Button>
        }
      />

      {isLoading ? (
        <PageListSearching />
      ) : (
        <>
          {/* サマリー */}
          <div className='mb-5 grid grid-cols-4 gap-3'>
            {[
              { label: 'サイト数', value: sites.length, colorClass: 'text-primary' },
              {
                label: '良好（70+）',
                value: sites.filter((s) => s.total_score >= 70).length,
                colorClass: 'text-success'
              },
              {
                label: '注意（40-69）',
                value: sites.filter((s) => s.total_score >= 40 && s.total_score < 70).length,
                colorClass: 'text-warning'
              },
              {
                label: '要対応（0-39）',
                value: sites.filter((s) => s.total_score < 40).length,
                colorClass: 'text-critical'
              }
            ].map(({ label, value, colorClass }) => (
              <div
                key={label}
                className='border-secondary bg-surface shadow-area rounded-xl border px-4 py-4 text-center'
              >
                <div className='text-secondary mb-1 text-xs'>{label}</div>
                <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* サイト一覧 */}
          <div className='flex flex-col gap-3'>
            {sites.map((site) => (
              <div
                key={site.site_id}
                className='border-secondary bg-surface shadow-area rounded-xl border px-4 py-4'
                style={{ borderLeftColor: scoreHex(site.total_score), borderLeftWidth: 4 }}
              >
                <div className='flex items-center gap-4'>
                  {/* スコアサークル */}
                  <div
                    className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white'
                    style={{ background: scoreHex(site.total_score) }}
                  >
                    {Math.round(site.total_score)}
                  </div>

                  {/* サイト情報 */}
                  <div className='flex-1'>
                    <div className='mb-2 flex items-center gap-2'>
                      <span className='text-primary text-sm font-bold'>{site.site_name}</span>
                      <span
                        className='text-size-10 rounded-full px-2 py-0.5 font-semibold'
                        style={{
                          background: `${scoreHex(site.total_score)}18`,
                          color: scoreHex(site.total_score)
                        }}
                      >
                        {scoreLabel(site.total_score)}
                      </span>
                      {site.alert && <span className='text-xs'>🔴</span>}
                    </div>

                    {/* 4軸スコアバー */}
                    <div className='grid grid-cols-4 gap-4'>
                      {SCORE_AXES.map((axis) => {
                        const score = site[axis.key]
                        const ratio = score / axis.max
                        return (
                          <div key={axis.label}>
                            <div className='text-secondary text-size-10 mb-1 flex justify-between'>
                              <span>{axis.label}</span>
                              <span className='text-primary font-semibold'>
                                {score}/{axis.max}
                              </span>
                            </div>
                            <div className='bg-fill-tertiary h-1.5 overflow-hidden rounded-full'>
                              <div
                                className='h-full rounded-full'
                                style={{
                                  width: `${ratio * 100}%`,
                                  background: scoreHex(ratio * 100)
                                }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* CS担当・更新日 */}
                  <div className='text-secondary text-size-10 shrink-0 text-right'>
                    {site.cs_owner && (
                      <div className='text-primary font-semibold'>{site.cs_owner}</div>
                    )}
                    <div>{site.calculated_at ? site.calculated_at.slice(0, 10) : '未算出'}</div>
                  </div>
                </div>
              </div>
            ))}

            {sites.length === 0 && (
              <div className='border-secondary bg-surface shadow-area rounded-xl border py-10 text-center'>
                <p className='text-secondary text-sm'>サイトがありません</p>
              </div>
            )}
          </div>
        </>
      )}
    </Main>
  )
}

export default SiteUsagePage
